import { useState, useRef, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";

interface NavHoverMenuProps {
  children: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

const ENTER_DELAY = 150;
const LEAVE_DELAY = 300;
const MD_BREAKPOINT = 768;

export function NavHoverMenu({ children, content, disabled }: NavHoverMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= MD_BREAKPOINT);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= MD_BREAKPOINT;
      setIsDesktop(desktop);
      if (!desktop) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const clearTimeouts = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (disabled || !isDesktop) return;

    clearTimeouts();
    enterTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, ENTER_DELAY);
  }, [disabled, isDesktop, clearTimeouts]);

  const handleMouseLeave = useCallback(() => {
    if (disabled || !isDesktop) return;

    clearTimeouts();
    leaveTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, LEAVE_DELAY);
  }, [disabled, isDesktop, clearTimeouts]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {/* Invisible bridge to prevent gap-close when moving to popover */}
      {isOpen && <div className="absolute left-0 right-0 top-full h-2" />}

      {/* Popover content */}
      <div
        className={cn(
          "absolute left-0 top-full mt-2 z-50",
          "rounded-md border bg-popover p-2 text-popover-foreground shadow-md",
          "max-h-[300px] overflow-y-auto",
          "transition-opacity duration-150",
          isOpen
            ? "opacity-100 animate-in fade-in-0"
            : "opacity-0 pointer-events-none"
        )}
      >
        {content}
      </div>
    </div>
  );
}
